import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { l1SignBotEndpoint } from '@/components/Wallet/SignaturePage'
import { ELECTIONS_ADDRESS } from '@/constants/blockchain'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useAuth } from '@/plugins/auth'
import { get } from '@/utils/utils'

import CampaignAction from '../GovernanceList/ListActions/CampaignAction'
import NomineesList from '../GovernanceList/NomineesList'

interface CampaignProps {}

const Campaign: React.FC<CampaignProps> = () => {
  const { t } = useTranslation()
  const { address } = useAuth()

  // Governance data
  const { nominationData, electionData } = useGovernanceContext()

  // Campaign Logic
  const [campaignLinks, setCampaignLinks] = useState<Map<string, string>>(new Map())

  const sessionCampaingLinksKey = useMemo(
    () => `campaignLinks-${electionData.idx}`,
    [electionData.idx]
  )
  const [loadingCampaignLinks, setLoadingCampaignLinks] = useState<boolean>(false)
  const campaignLinkKeyCondition = useMemo(
    () => !address || electionData.idx === 0,
    [address, electionData.idx]
  )

  const fetchDiscordLinks = useCallback(async () => {
    setLoadingCampaignLinks(true)
    try {
      const response = await get(
        `https://${l1SignBotEndpoint}/get-candidate-channels?electionContract=${encodeURIComponent(
          ELECTIONS_ADDRESS
        )}`
      )
      const candidateMap: Record<string, string> = response
      if (!candidateMap) {
        console.error('No candidate channels found')
      } else {
        const map = new Map<string, string>(Object.entries(candidateMap))
        setCampaignLinks(map)
        sessionStorage.setItem(sessionCampaingLinksKey, JSON.stringify([...map]))
      }
    } catch (error) {
      console.error('Error fetching candidate channels:', error)
    } finally {
      setLoadingCampaignLinks(false)
    }
  }, [sessionCampaingLinksKey])

  useEffect(() => {
    if (campaignLinkKeyCondition) return
    const sessionCampaignLinks = sessionStorage.getItem(sessionCampaingLinksKey)
    if (!sessionCampaignLinks) {
      fetchDiscordLinks()
    } else {
      setCampaignLinks(new Map(JSON.parse(sessionCampaignLinks)))
    }
  }, [campaignLinkKeyCondition, sessionCampaingLinksKey, fetchDiscordLinks])

  return (
    <NomineesList
      title={t('components.governance.campaign.title', 'Campaign')}
      candidates={nominationData.filter(
        cand => cand.status === 'ACCEPTED' || cand.status === 'REMOVED'
      )}
      phase="campaign"
      action={CampaignAction}
      actionProps={{
        campaignLinks,
        loadingCampaignLinks,
        nominee: '',
        status: 'ACCEPTED',
        isBreakpoint: false,
      }}
    />
  )
}

export default Campaign
